import { useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

const MONTH_LABELS = Array.from({ length: 12 }, (_, month) =>
  format(new Date(2000, month, 1), 'MMM')
);

interface Props {
  selected?: Date;
  onSelect: (date: Date) => void;
  placeholder?: string;
  disabledDates?: (date: Date) => boolean;
  fromYear?: number;
  toYear?: number;
}

/**
 * A month/year-only picker, distinct from `DatePicker`: fields like a
 * certification's completion date only ever need a month and year, so a full
 * day-grid calendar - `DatePicker` formatted to "MMM yyyy" but still asking
 * for a day to be clicked - was the wrong control, not just the wrong label.
 */
export function MonthYearPicker({
  selected,
  onSelect,
  placeholder,
  disabledDates,
  fromYear = 1980,
  toYear = new Date().getFullYear(),
}: Props) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(selected?.getFullYear() ?? toYear);

  // Newest first: a recently-earned certification is the common case.
  const years = Array.from(
    { length: toYear - fromYear + 1 },
    (_, index) => toYear - index
  );

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setViewYear(selected?.getFullYear() ?? toYear);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start text-left font-normal"
          square
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selected ? (
            format(selected, 'MMM yyyy')
          ) : (
            <span className="text-muted-foreground">
              {placeholder || 'Select month/year'}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3">
        <div className="mb-2 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={viewYear <= fromYear}
            onClick={() => setViewYear((year) => year - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Select
            value={String(viewYear)}
            onValueChange={(value) => setViewYear(Number(value))}
          >
            <SelectTrigger className="h-8 w-24 border-none text-center text-sm font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {years.map((year) => (
                <SelectItem key={year} value={String(year)}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={viewYear >= toYear}
            onClick={() => setViewYear((year) => year + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {MONTH_LABELS.map((label, month) => {
            const date = new Date(viewYear, month, 1);
            const isSelected =
              selected?.getFullYear() === viewYear &&
              selected?.getMonth() === month;

            return (
              <Button
                key={label}
                type="button"
                variant={isSelected ? 'default' : 'outline'}
                disabled={disabledDates?.(date) ?? false}
                className="h-9"
                onClick={() => {
                  onSelect(date);
                  setOpen(false);
                }}
              >
                {label}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
